import {FC, MouseEvent, MutableRefObject, useId} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextVariant} from '@/components/common/content/Text'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {theme} from '@/theme/theme'

import {CollapsibleSubmenu} from '../submenu/CollapsibleSubmenu'
import {SubmenuLayout} from '../submenu/SubmenuLayout'

type SubmenuLinkProps = {
  label: string
  isActive: boolean
  isSwitching?: boolean
  panelHeightRef?: MutableRefObject<number>
  onClick?: (event: MouseEvent<HTMLElement>) => void
  isMobile?: boolean
}

export const SubmenuLink: FC<React.PropsWithChildren<SubmenuLinkProps>> = ({
  label,
  isActive,
  isSwitching,
  panelHeightRef,
  onClick,
  isMobile,
  children,
}) => {
  const submenuId = useId()

  if (isMobile) {
    return (
      <>
        <div className="label" onClick={onClick} role="button" tabIndex={0}>
          <Text variant={TextVariant.subtitle12} color={theme.palette.text.white}>
            {label}
          </Text>
          <ChevronIcon isActive={isActive} color={theme.palette.text.white} />
        </div>
        <CollapsibleSubmenu isActive={isActive}>
          <div className="wrapper">
            <SubmenuLayout isVisible={isActive}>{children}</SubmenuLayout>
          </div>
        </CollapsibleSubmenu>

        <style jsx>{`
          .label {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: ${theme.spacing[3]} 0;
            cursor: pointer;
          }
          .wrapper {
            margin: ${theme.spacing[3]} 0 ${theme.spacing[5]};
          }
        `}</style>
      </>
    )
  }

  return (
    <>
      <Button
        type="button"
        size={ButtonSize.small}
        variant={ButtonVariant.darkGray}
        hasBorder={false}
        icon={<ChevronIcon isActive={isActive} color={theme.palette.text.white} />}
        onClick={onClick}
        ariaExpanded={isActive}
        ariaControls={submenuId}
      >
        {label}
      </Button>
      <SubmenuLayout
        id={submenuId}
        isVisible={isActive}
        isSwitching={isSwitching}
        panelHeightRef={panelHeightRef}
      >
        {children}
      </SubmenuLayout>
    </>
  )
}
